import { Zap, RefreshCw, TrendingUp, Infinity, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function Services() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  return (
    <section id="services" className="py-20 md:py-32 px-6 bg-deep-navy">
      <div ref={ref} className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 scroll-fade-in ${isVisible ? 'visible' : ''}`}>
          <div className="inline-block px-4 py-1 rounded-full border border-cyan/30 bg-cyan/5 mb-6 text-xs text-cyan font-bold uppercase tracking-wider">
            Development Services
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Intelligent Systems That Drive Results
          </h2>
          <p className="text-lg md:text-xl text-light-gray/80 max-w-3xl mx-auto">
            Strategic clarity meets execution mastery. Our systems deliver measurable ROI and scale with your ambitions.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-12 mb-12">
          {/* Card 1: AI Growth Engine */}
          <div className={`bg-dark-card rounded-xl border border-cyan/10 p-8 hover:border-cyan/30 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-cyan/10 hover:scale-[1.02] scroll-slide-in-left ${isVisible ? 'visible' : ''}`}>
            <div className="inline-block px-3 py-1 rounded-full bg-cyan/10 text-cyan text-xs font-bold uppercase mb-4">
              Most Requested
            </div>
            <div className="w-14 h-14 rounded-lg bg-cyan/10 flex items-center justify-center mb-6">
              <Zap className="w-7 h-7 text-cyan" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-3">
              Turn Leads Into Booked Calls
            </h3>
            <p className="text-base text-light-gray/80 mb-4">
              AI-driven lead capture, qualification, and follow-up that books meetings on autopilot. Your calendar stays full while you focus on closing deals.
            </p>
            <div className="flex items-center gap-4 mb-6">
              <p className="text-sm text-cyan font-semibold">
                210% more calls
              </p>
              <span className="text-medium-gray">•</span>
              <p className="text-sm text-cyan font-semibold">
                80% faster response
              </p>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                "24/7 AI-powered lead qualification",
                "CRM & calendar sync (Salesforce, HubSpot, etc.)",
                "Conversion tracking & analytics",
                "Custom qualification rules",
                "Multi-channel follow-up (email, SMS, WhatsApp)",
              ].map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                  <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              className="w-full bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200"
            >
              <a href="https://calendly.com/arqeraai-strategy-call/" target="_blank" rel="noopener noreferrer">
                Learn More & Get Custom Plan
              </a>
            </Button>
          </div>

          {/* Card 2: Database Revival - HIGHLIGHTED */}
          <div className={`bg-dark-card rounded-xl border-2 border-cyan/50 p-8 hover:border-cyan/70 hover:-translate-y-3 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan/30 scale-105 relative scroll-scale-in ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '150ms' }}>
            <div className="absolute -top-3 right-4 px-4 py-1 rounded-full bg-cyan text-white text-xs font-bold uppercase shadow-lg">
              HIGHEST ROI
            </div>
            <div className="w-14 h-14 rounded-lg bg-cyan/10 flex items-center justify-center mb-6">
              <RefreshCw className="w-7 h-7 text-cyan rotate-on-hover" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-3">
              Wake Your Dormant Pipeline
            </h3>
            <p className="text-base text-light-gray/80 mb-4">
              Intelligent reactivation of dormant leads. AI-personalized outreach transforms forgotten contacts into revenue. Guaranteed ROI in 14 days.
            </p>
            <div className="flex items-center gap-4 mb-6">
              <p className="text-sm text-cyan font-semibold">
                $450K avg ARR
              </p>
              <span className="text-medium-gray">•</span>
              <p className="text-sm text-cyan font-semibold">
                3× qualified calls
              </p>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                "Multi-channel smart outreach (email, LinkedIn, SMS)",
                "AI-personalized message generation",
                "Automated follow-up sequences",
                "Lead scoring & priority ranking",
                "14-day ROI guarantee",
              ].map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                  <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              className="w-full bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg"
            >
              <a href="https://calendly.com/arqeraai-strategy-call/" target="_blank" rel="noopener noreferrer">
                Activate My Database
              </a>
            </Button>
          </div>

          {/* Card 3: AI Systems Audit */}
          <div className={`bg-dark-card rounded-xl border border-cyan/10 p-8 hover:border-cyan/30 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-cyan/10 hover:scale-[1.02] scroll-slide-in-right ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '300ms' }}>
            <div className="inline-block px-3 py-1 rounded-full bg-cyan/10 text-cyan text-xs font-bold uppercase mb-4">
              For Scaling Founders
            </div>
            <div className="w-14 h-14 rounded-lg bg-cyan/10 flex items-center justify-center mb-6">
              <TrendingUp className="w-7 h-7 text-cyan" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-3">
              Find Hidden Growth Gaps
            </h3>
            <p className="text-base text-light-gray/80 mb-4">
              Deep audit of your operations. We identify bottlenecks, quantify opportunity, and deliver a 90-day transformation roadmap with clear ROI projections.
            </p>
            <div className="flex items-center gap-4 mb-6">
              <p className="text-sm text-cyan font-semibold">
                $200K+ opportunity
              </p>
              <span className="text-medium-gray">•</span>
              <p className="text-sm text-cyan font-semibold">
                70% fewer manual tasks
              </p>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                "End-to-end sales process mapping",
                "Competitive AI benchmarking",
                "Opportunity quantification",
                "90-day implementation plan",
                "Executive strategy report",
              ].map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                  <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              className="w-full bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200"
            >
              <a href="https://calendly.com/arqeraai-strategy-call/" target="_blank" rel="noopener noreferrer">
                Book Your Audit
              </a>
            </Button>
          </div>
        </div>

        {/* Card 4: Advanced Solutions - Full Width */}
        <div className="bg-medium-navy rounded-2xl border-t-4 border-cyan/40 p-12 md:p-16 hover:border-cyan/60 hover:-translate-y-1 transition-all duration-300 shadow-lg">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1 rounded-full bg-cyan/10 text-cyan text-xs font-bold uppercase tracking-wider mb-4">
              Enterprise Grade
            </div>
            <div className="w-16 h-16 rounded-lg bg-cyan/10 flex items-center justify-center mx-auto mb-6">
              <Infinity className="w-9 h-9 text-cyan" />
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              Build Your Unfair Advantage
            </h3>
            <p className="text-lg text-light-gray/80 max-w-3xl mx-auto">
              For founders ready to own their AI stack. Private infrastructure + autonomous operations = unstoppable growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 mb-10">
            {/* Left Column */}
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-cyan/10 flex items-center justify-center">
                  <Server className="w-6 h-6 text-cyan" />
                </div>
                <h4 className="text-xl font-bold text-foreground">
                  Own Your AI. Own Your Data.
                </h4>
              </div>
              <p className="text-base text-light-gray/80 mb-6">
                Design your private LLM stack. Secure, scalable, fully owned. From GPU cluster architecture to governance frameworks—we architect your competitive advantage. No vendor lock-in. Total control.
              </p>
              <ul className="space-y-3">
                {[
                  "Custom infrastructure design & deployment",
                  "Private LLM fine-tuning & optimization",
                  "Security & compliance architecture (HIPAA, SOC2, GDPR)",
                  "24/7 monitoring & SLA guarantees (99.9% uptime)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                    <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column */}
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-cyan/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-cyan" />
                </div>
                <h4 className="text-xl font-bold text-foreground">
                  AI That Runs Your Operations
                </h4>
              </div>
              <p className="text-base text-light-gray/80 mb-6">
                Autonomous agents designed for your business. Onboarding automation. CRM intelligence. Analytics workflows. Scale your output without scaling your team.
              </p>
              <ul className="space-y-3">
                {[
                  "Custom agent architecture & design",
                  "Workflow automation for your specific processes",
                  "Integration with your existing tools",
                  "Performance monitoring & continuous optimization",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-light-gray">
                    <Check className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan/50 w-full sm:w-auto px-10 py-6"
            >
              <Link to="/advanced-solutions">
                Schedule Infrastructure Consultation
              </Link>
            </Button>
          </div>
        </div>

        {/* Audit CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-bronze/5 to-cyan/5 rounded-2xl border border-bronze/20 p-10 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Not Sure Where AI Creates the Most Leverage?
          </h3>
          <p className="text-light-gray/90 mb-6 max-w-2xl mx-auto">
            Take our 2-minute AI Transformation Audit and discover your highest-ROI opportunities — personalized to your business context.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-bronze hover:bg-bronze-muted text-white font-semibold hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <Link to="/ai-transformation-audit">
              Map Your Fastest AI ROI → Take the Audit
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
